import { useEffect, useState } from "react";
import {
    listQuoteTracking,
    createQuoteTracking,
    updateQuoteTracking,
    deleteQuoteTracking,
} from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { hasFeature } from "@/lib/tier";
import LockedFeatureCard from "@/components/investor/LockedFeatureCard";
import { Plus, Trash2, ClipboardList } from "lucide-react";
import { toast } from "sonner";

const STATUS_LABEL = {
    requested: "Requested",
    contacted: "Contacted",
    quote_received: "Quote received",
    follow_up_needed: "Follow-up",
    closed: "Closed",
    cancelled: "Cancelled",
};

const STATUS_TONE = {
    requested: "bg-bl-teal-tint text-bl-primary",
    contacted: "bg-blue-100 text-blue-700",
    quote_received: "bg-emerald-100 text-emerald-700",
    follow_up_needed: "bg-amber-100 text-amber-800",
    closed: "bg-bl-bg text-bl-muted",
    cancelled: "bg-bl-bg text-bl-muted line-through",
};

const EMPTY_DRAFT = {
    category: "",
    contractor_name: "",
    quoted_amount: "",
    status: "requested",
    notes: "",
};

/**
 * QuoteTrackingPanel — Pro+ workflow for tracking contractor quotes manually.
 * Cost-Safe: pure DB CRUD. Never invokes AI.
 */
export default function QuoteTrackingPanel() {
    const { user } = useAuth();
    const canAccess = hasFeature(user, "quote_tracking_dashboard");
    const [data, setData] = useState({ items: [], counts: {}, statuses: [] });
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("all");
    const [draft, setDraft] = useState(EMPTY_DRAFT);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!canAccess) return undefined;
        let cancelled = false;
        (async () => {
            try {
                const r = await listQuoteTracking();
                if (!cancelled) setData(r);
            } catch (e) {
                if (!cancelled) {
                    toast.error(e?.response?.data?.detail?.message || "Could not load quotes.");
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [canAccess]);

    if (!canAccess) {
        return (
            <div className="max-w-2xl">
                <LockedFeatureCard
                    feature="quote_tracking_dashboard"
                    title="Quote tracking dashboard"
                    description="Track every contractor outreach — requested, contacted, quoted, follow-ups — without leaving Buyer's Leverage. Upgrade to Pro to unlock."
                />
            </div>
        );
    }

    const filtered =
        filter === "all"
            ? data.items
            : data.items.filter((i) => i.status === filter);

    const onCreate = async (e) => {
        e.preventDefault();
        if (!draft.category && !draft.contractor_name) {
            toast.error("Add a category or contractor name.");
            return;
        }
        setSubmitting(true);
        try {
            const payload = {
                category: draft.category || null,
                contractor_name: draft.contractor_name || null,
                quoted_amount: draft.quoted_amount === "" ? null : Number(draft.quoted_amount),
                status: draft.status,
                notes: draft.notes || null,
            };
            const created = await createQuoteTracking(payload);
            setData((d) => ({
                ...d,
                items: [created, ...d.items],
                counts: {
                    ...d.counts,
                    [created.status]: (d.counts[created.status] || 0) + 1,
                },
            }));
            setDraft(EMPTY_DRAFT);
            toast.success("Quote logged.");
        } catch (e) {
            toast.error(e?.response?.data?.detail?.message || "Could not log quote.");
        } finally {
            setSubmitting(false);
        }
    };

    const onStatusChange = async (qid, next) => {
        try {
            const updated = await updateQuoteTracking(qid, { status: next });
            setData((d) => {
                const prev = d.items.find((i) => i.quote_track_id === qid)?.status;
                const counts = { ...d.counts };
                if (prev) counts[prev] = Math.max(0, (counts[prev] || 0) - 1);
                counts[next] = (counts[next] || 0) + 1;
                return {
                    ...d,
                    items: d.items.map((i) =>
                        i.quote_track_id === qid ? updated : i,
                    ),
                    counts,
                };
            });
        } catch (e) {
            toast.error(e?.response?.data?.detail?.message || "Could not update.");
        }
    };

    const onDelete = async (qid) => {
        if (!confirm("Delete this quote entry?")) return;
        try {
            const item = data.items.find((i) => i.quote_track_id === qid);
            await deleteQuoteTracking(qid);
            setData((d) => ({
                ...d,
                items: d.items.filter((i) => i.quote_track_id !== qid),
                counts: item
                    ? {
                          ...d.counts,
                          [item.status]: Math.max(0, (d.counts[item.status] || 0) - 1),
                      }
                    : d.counts,
            }));
        } catch (e) {
            toast.error(e?.response?.data?.detail?.message || "Could not delete.");
        }
    };

    return (
        <div className="space-y-5" data-testid="quote-tracking-panel">
            {/* Status filter chips */}
            <div className="flex flex-wrap gap-2">
                <button
                    onClick={() => setFilter("all")}
                    className={
                        "px-3 py-1.5 rounded-full text-xs " +
                        (filter === "all"
                            ? "bg-bl-primary text-bl-surface"
                            : "bg-bl-teal-tint/40 text-bl-primary hover:bg-bl-teal-tint")
                    }
                    data-testid="qt-filter-all"
                >
                    All · {data.items.length}
                </button>
                {data.statuses.map((s) => (
                    <button
                        key={s}
                        onClick={() => setFilter(s)}
                        className={
                            "px-3 py-1.5 rounded-full text-xs " +
                            (filter === s
                                ? "bg-bl-primary text-bl-surface"
                                : "bg-bl-teal-tint/40 text-bl-primary hover:bg-bl-teal-tint")
                        }
                        data-testid={`qt-filter-${s}`}
                    >
                        {STATUS_LABEL[s]} · {data.counts[s] || 0}
                    </button>
                ))}
            </div>

            {/* Add quote form */}
            <form
                onSubmit={onCreate}
                className="bl-card p-4 md:p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3"
                data-testid="qt-add-form"
            >
                <input
                    type="text"
                    placeholder="Category (Roofing, HVAC…)"
                    value={draft.category}
                    onChange={(e) => setDraft({ ...draft, category: e.target.value })}
                    className="px-3 py-2 rounded-md border border-bl bg-bl-surface text-bl text-sm"
                    data-testid="qt-input-category"
                />
                <input
                    type="text"
                    placeholder="Contractor name"
                    value={draft.contractor_name}
                    onChange={(e) =>
                        setDraft({ ...draft, contractor_name: e.target.value })
                    }
                    className="px-3 py-2 rounded-md border border-bl bg-bl-surface text-bl text-sm"
                    data-testid="qt-input-contractor"
                />
                <input
                    type="number"
                    placeholder="Quoted amount $"
                    min="0"
                    value={draft.quoted_amount}
                    onChange={(e) =>
                        setDraft({ ...draft, quoted_amount: e.target.value })
                    }
                    className="px-3 py-2 rounded-md border border-bl bg-bl-surface text-bl text-sm"
                    data-testid="qt-input-amount"
                />
                <select
                    value={draft.status}
                    onChange={(e) => setDraft({ ...draft, status: e.target.value })}
                    className="px-3 py-2 rounded-md border border-bl bg-bl-surface text-bl text-sm"
                    data-testid="qt-input-status"
                >
                    {data.statuses.map((s) => (
                        <option key={s} value={s}>
                            {STATUS_LABEL[s]}
                        </option>
                    ))}
                </select>
                <button
                    type="submit"
                    disabled={submitting}
                    className="bl-btn-primary text-sm"
                    data-testid="qt-add-btn"
                >
                    <Plus className="w-4 h-4" /> Log
                </button>
                <input
                    type="text"
                    placeholder="Notes (optional)"
                    value={draft.notes}
                    onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
                    className="px-3 py-2 rounded-md border border-bl bg-bl-surface text-bl text-sm sm:col-span-2 lg:col-span-5"
                    data-testid="qt-input-notes"
                />
            </form>

            {/* List */}
            {loading ? (
                <div className="text-sm text-bl-muted">Loading quotes…</div>
            ) : filtered.length === 0 ? (
                <div className="bl-card p-8 text-center" data-testid="qt-empty">
                    <ClipboardList className="w-6 h-6 text-bl-muted mx-auto" />
                    <p className="mt-3 text-sm text-bl-muted">
                        No quotes {filter === "all" ? "logged yet" : "in this status"}.
                    </p>
                </div>
            ) : (
                <div className="bl-card overflow-x-auto" data-testid="qt-table">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left border-b border-bl">
                                <th className="px-4 py-3 text-[10px] tracking-[0.18em] uppercase text-bl-muted font-medium">
                                    Category
                                </th>
                                <th className="px-4 py-3 text-[10px] tracking-[0.18em] uppercase text-bl-muted font-medium">
                                    Contractor
                                </th>
                                <th className="px-4 py-3 text-[10px] tracking-[0.18em] uppercase text-bl-muted font-medium">
                                    Amount
                                </th>
                                <th className="px-4 py-3 text-[10px] tracking-[0.18em] uppercase text-bl-muted font-medium">
                                    Status
                                </th>
                                <th className="px-4 py-3 text-[10px] tracking-[0.18em] uppercase text-bl-muted font-medium">
                                    Notes
                                </th>
                                <th className="px-4 py-3" />
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((q) => (
                                <tr
                                    key={q.quote_track_id}
                                    className="border-b border-bl last:border-0 hover:bg-bl-bg/40"
                                    data-testid={`qt-row-${q.quote_track_id}`}
                                >
                                    <td className="px-4 py-3 text-bl">
                                        {q.category || "—"}
                                    </td>
                                    <td className="px-4 py-3 text-bl">
                                        {q.contractor_name || "—"}
                                    </td>
                                    <td className="px-4 py-3 font-mono-bl">
                                        {q.quoted_amount != null
                                            ? `$${Number(q.quoted_amount).toLocaleString()}`
                                            : "—"}
                                    </td>
                                    <td className="px-4 py-3">
                                        <select
                                            value={q.status}
                                            onChange={(e) =>
                                                onStatusChange(q.quote_track_id, e.target.value)
                                            }
                                            className={
                                                "text-xs px-2 py-1 rounded-md border border-bl/40 " +
                                                STATUS_TONE[q.status]
                                            }
                                            data-testid={`qt-status-${q.quote_track_id}`}
                                        >
                                            {data.statuses.map((s) => (
                                                <option key={s} value={s}>
                                                    {STATUS_LABEL[s]}
                                                </option>
                                            ))}
                                        </select>
                                    </td>
                                    <td className="px-4 py-3 text-bl-muted text-xs max-w-[18rem] truncate">
                                        {q.notes || "—"}
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                        <button
                                            onClick={() => onDelete(q.quote_track_id)}
                                            className="text-bl-muted p-1.5 rounded hover:bg-[#C85B3A]/10 hover:text-[#C85B3A]"
                                            aria-label="Delete quote entry"
                                            data-testid={`qt-delete-${q.quote_track_id}`}
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <p className="text-xs text-bl-muted leading-relaxed">
                Quotes are manually managed — Buyer's Leverage doesn't auto-reach
                out to contractors. Track your outreach and decisions here.
            </p>
        </div>
    );
}
